import React, { useState, useEffect } from "react";
import { Grid, Button, FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import clsx from "clsx";
import GuestTypeService from "../../../services/GuestTypeService";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import apiClient from "../../../auth/apiClient";
import ChipMultiSelectWithDelete from "../../Dropdowns/ChipMultiSelectWithDelete/ChipMultiSelectWithDelete";
import SearchBar from "../../SearchBar";
import { useStyles } from "./ValetInventoryHistoricalSearch.styles";

const MAX_DATE_RANGE_DAYS = 30;
const MIN_SEARCH_TERM_LENGTH = 3;

const searchContextOptions = [
  { id: 0, value: "Ticket Number" },
  { id: 1, value: "License Plate" },
  { id: 2, value: "Make" },
  { id: 3, value: "Model" },
  { id: 4, value: "Color" },
  { id: 5, value: "Rate" },
  { id: 6, value: "Customer Name" }
];

const valetInventorySearchSchema = Yup.object().shape({
  fromDate: Yup.date()
    .required("From date is required")
    .max(Yup.ref('toDate'), "From date cannot be after To date")
    .test(
      "date-range",
      `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`,
      function (fromDate) {
        const toDate = this.parent.toDate;
        if (!fromDate || !toDate) return true;
        return moment(toDate).diff(moment(fromDate), 'days') < MAX_DATE_RANGE_DAYS;
      }
    ),
  toDate: Yup.date()
    .required("To date is required")
    .min(Yup.ref('fromDate'), "To date cannot be before From date")
    .test(
        "date-range",
        `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`,
        function (toDate) {
          const fromDate = this.parent.fromDate;
          if (!fromDate || !toDate) return true;
          return moment(toDate).diff(moment(fromDate), 'days') < MAX_DATE_RANGE_DAYS;
        }
      ),
  searchContexts: Yup.array()
    .of(Yup.number())
    .default([]),
  searchTerm: Yup.string()
    .when("searchContexts", {
      is: (searchContexts) => searchContexts.length > 0,
      then: (schema) => schema
        .min(MIN_SEARCH_TERM_LENGTH, `Search term must be at least ${MIN_SEARCH_TERM_LENGTH} characters`)
        .required("Search term is required when search contexts are selected"),
      otherwise: (schema) => schema.notRequired(),
    }),
  guestType: Yup.string().default("")
});

const guestTypeService = new GuestTypeService(apiClient);

const ValetInventoryHistoricalSearch = ({ onSearch, loading }) => {
  const classes = useStyles();
  const { facilityID: entityID } = useCurrentFacility();
  const { timeZone } = useCurrentFacilityTimezone();

  const { control, handleSubmit, watch, setValue, formState: { errors }, trigger } = useForm({
    resolver: yupResolver(valetInventorySearchSchema),
    defaultValues: {
      searchContexts: [],
      searchTerm: "",
      guestType: "",
      device: "",
      fromDate: moment().subtract(1, "day"),
      toDate: moment()
    },
    mode: 'onChange'
  });

  const [guestTypes, setGuestTypes] = useState([]);

  useEffect(() => {
    const loadGuestTypes = async () => {
      try {
        if (entityID) {
          const response = await guestTypeService.getGuestTypes(entityID);
          setGuestTypes(response.data);
        }
      } catch (error) {
        console.error("Error loading guest types:", error);
      }
    };

    loadGuestTypes();
  }, [entityID]);

  const getSelectedSearchContexts = (contextIds) => {
    if (!contextIds || contextIds.length === 0) return [];
    try {
      return searchContextOptions.filter(ctx => contextIds.includes(ctx.id));
    } catch (err) {
      return [];
    }
  };

  const isSearchDisabled = () => {
    return Object.keys(errors).length > 0;
  };

  const handleDateChange = (date, field) => {
    field.onChange(date);
    trigger(["fromDate", "toDate"]);
  };
  
  const handleSearchContextChange = (optionsChosen) => {
    const values = optionsChosen.map(option => option.id);
    setValue('searchContexts', values);
    trigger(['searchContexts', 'searchTerm']);
  };

  const onSubmit = (data) => {
    const fromDateFormatted = moment(data.fromDate).tz(timeZone).startOf('day').utc().toISOString();
    const toDateFormatted = moment(data.toDate).tz(timeZone).endOf('day').utc().toISOString();

    onSearch({
      searchContexts: data.searchContexts,
      searchTerm: data.searchTerm.trim(),
      guestType: data.guestType,
      fromDate: fromDateFormatted,
      toDate: toDateFormatted
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container alignItems="center" className={clsx(classes.inputsGrid, "valet-inventory-history-search")}>
        <Grid item xs={12} sm={6} md={3} className={classes.dateGrid}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <Controller
              name="fromDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <KeyboardDatePicker
                  id="fromDate"
                  label="From"
                  disableFuture
                  format={"MM/DD/YYYY"}                  
                  value={field.value}
                  onChange={(date) => handleDateChange(date, field)}
                  onBlur={field.onBlur}
                  error={!!error}
                  helperText={error?.message}
                  className={clsx("from-date")}
                />
              )}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.dateGrid}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <Controller
              name="toDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <KeyboardDatePicker
                  id="toDate"
                  label="To"
                  disableFuture
                  format={"MM/DD/YYYY"}
                  value={field.value}
                  onChange={(date) => handleDateChange(date, field)}
                  onBlur={field.onBlur}
                  error={!!error}
                  helperText={error?.message}
                  className={clsx("to-date")}
                />
              )}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.inputGrid}>
          <FormControl variant="standard" fullWidth size="small">
            <InputLabel id="guest-type-label">Guest Type</InputLabel>
            <Controller
              name="guestType"
              control={control}
              render={({ field }) => (
                <Select
                  labelId="guest-type-label"
                  id="guest-type"
                  value={field.value}
                  onChange={field.onChange}
                  label="Guest Type"
                  className={clsx("guest-type-dropdown")}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {guestTypes.map((guestType) => (
                    <MenuItem key={guestType.guestTypeID} value={guestType.guestTypeID}>
                      {guestType.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3} className={classes.inputGrid}>
          <FormControl variant="standard" fullWidth size="small">
            <InputLabel id="device-label">Device</InputLabel>
            <Controller
              name="device"
              control={control}
              render={({ field }) => (
                <Select
                  labelId="device-label"
                  id="device"
                  value={field.value}
                  onChange={field.onChange}
                  label="Device"
                  className={clsx("device-dropdown")}
                >                  
                  {/* TODO: Add actual device options when available */}
                </Select>
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={6} className={classes.inputGrid}>
          <Controller
            name="searchContexts"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <ChipMultiSelectWithDelete
                className={classes.searchContextSelector}
                identifier="searchContexts"
                options={searchContextOptions}
                onChange={handleSearchContextChange}
                selected={getSelectedSearchContexts(field.value)}
                placeholder={error?.message || "Search For..."}
                isError={!!error}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} className={classes.inputGrid}>
          <Controller
            name="searchTerm"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SearchBar
                name="searchTermBar"
                label="Min 3 char"
                placeholder="Search..."
                data-id={"search-term-bar"}
                delay={0}
                value={field.value}
                onChange={(text) => field.onChange(text?.trim() || "")}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} className={classes.buttonGrid}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || isSearchDisabled()}
            className={clsx(classes.searchButton, "search-button")}
          >
            SEARCH
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ValetInventoryHistoricalSearch;
